/**
 * Import the default markdown schema, and modify it as needed.
 *
 * ProseMirror compares node and mark types by identity, so it is important that
 * all code that uses node types references the same instance exported from this
 * module.
 *
 */

import { DOMOutputSpec, Schema, Node } from "prosemirror-model";
import { schema as markdownSchema } from "prosemirror-markdown";

// Here we can override the basic schema
const originalSchema = new Schema({
    nodes: markdownSchema.spec.nodes,
    marks: markdownSchema.spec.marks,
});

/**
 * Higher-order function that enhances ProseMirror's DOM output specification by adding
 * CSS part attributes to nodes. This enables styling of specific node types through
 * the Shadow DOM boundary.
 *
 * @param nodeName - The name of the ProseMirror node type (e.g., "heading", "paragraph")
 * @param toDOM - The original ProseMirror DOM conversion function for the node
 * @returns A new DOM conversion function that wraps the original and adds part attributes
 *          The returned function converts a node to a DOMOutputSpec with added CSS part
 *          attributes in the format `node-{nodetype}` or `node-heading-{level}`
 *
 * @example
 * // For a heading node, converts:
 * // ["h1", "Hello World"]
 * // into:
 * // ["h1", { part: "node-heading-1" }, "Hello World"]
 */
const taggedToDOM =
    (nodeName: string, toDOM: (node: Node) => DOMOutputSpec) =>
    (node: Node): DOMOutputSpec => {
        const dom = toDOM(node);
        if (dom instanceof Array) {
            const hasAttributes =
                dom &&
                dom.length > 0 &&
                dom[1] !== null &&
                typeof dom[1] === "object" &&
                // When the object is an array of nested nodes (arrays are objects in
                // js), they by definition won't have any attributes.
                !Array.isArray(dom[1]);

            const taggedToDOM: [string, ...unknown[]] = [
                dom[0],
                {
                    // Extract information about the node type and reflect it using 'part'
                    // so the node can be styled using CSS.
                    part: `node-${nodeToPartName(nodeName, node)}`,
                    ...(hasAttributes ? dom[1] : null),
                },
                ...dom.slice(hasAttributes ? 2 : 1),
            ];
            return taggedToDOM;
        }
        return dom;
    };

const nodeToPartName = (nodeName: string, node: Node): string => {
    if (nodeName === "heading") {
        return `heading-${node.attrs.level}`;
    }
    // Replace underscores with dashes because they are more common in css identifiers
    return nodeName.replace("_", "-");
};

let taggedNodesSpec = originalSchema.spec.nodes;

taggedNodesSpec.forEach((name, spec) => {
    taggedNodesSpec = taggedNodesSpec.update(name, {
        ...spec,
        toDOM: spec.toDOM ? taggedToDOM(name, spec.toDOM) : undefined,
    });
});

const taggedSchema = new Schema({
    nodes: taggedNodesSpec,
    marks: originalSchema.spec.marks,
});

export { taggedSchema as Schema };
